import React, { Fragment, useMemo, useEffect, useRef } from "react";
import { Card, Table, Row, Col } from "react-bootstrap";
import moment from "moment-timezone";

// Transform Airflow data to match the expected format
function transformAirflowData(airflowData) {
  console.log(
    "%cTransforming Airflow data for Cluster Pool 1...",
    "color: blue; font-weight: bold; font-size: 16px;"
  );
  console.log("airflowData received:", airflowData);
  
  const today = moment().format('YYYY-MM-DD'); // Get today's date
  console.log("Today's date:", today);
  
  const result = { FA: {}, Construction: {}, Common: {} };

  if (!Array.isArray(airflowData)) {
    console.log("airflowData is not an array:", typeof airflowData);
    return result;
  }

  console.log("Processing", airflowData.length, "DAGs");

  airflowData.forEach((dag, index) => {
    console.log(`Processing DAG ${index + 1}:`, dag);
    
    // Use includes() to match DAG IDs more flexibly
    let colKey = null;
    const dagId = dag.dag_id.toLowerCase();
    
    if (dagId.includes('construction')) {
      colKey = 'Construction';
    } else if (dagId.includes('fa')) {
      colKey = 'FA';
    } else if (dagId.includes('common')) {
      colKey = 'Common';
    }
    
    if (!colKey) {
      console.warn(`Skipping unknown DAG ID: ${dag.dag_id}`);
      return;
    }

    console.log(`DAG ${dag.dag_id} mapped to column: ${colKey}`);

    if (Array.isArray(dag.runs) && dag.runs.length > 0) {
      console.log(`Processing ${dag.runs.length} runs for DAG ${dag.dag_id}`);
      
      // Filter runs to only include today's runs, then group by hour
      const todaysRuns = dag.runs.filter(run => {
        const runMoment = moment(run.start_date);
        const runDate = runMoment.format('YYYY-MM-DD');
        const isToday = runDate === today;
        console.log(`Run ${run.run_id}: ${run.start_date} -> ${runDate} (Today: ${isToday}) [Today is: ${today}]`);
        return isToday;
      });
      
      if (todaysRuns.length === 0) {
        console.log(`No runs found for today (${today}) for DAG ${dag.dag_id}`);
        return;
      }
      
      // Group today's runs by hour and get the latest run for each hour
      const runsByHour = {};
      
      todaysRuns.forEach(run => {
        const runMoment = moment.tz(run.start_date, "UTC").tz("America/New_York");
        const hourKey = runMoment.format('h:mm A'); // e.g., "1:00 AM"
        const displayTime = runMoment.format('h:mm A'); // e.g., "1:30 AM" (removed seconds)
        
        console.log(`Run ${run.run_id}: ${run.start_date} -> ${displayTime} (Hour: ${hourKey})`);
        
        // If we don't have a run for this hour yet, or this run is later than the existing one
        if (!runsByHour[hourKey] || moment(run.start_date).isAfter(moment(runsByHour[hourKey].start_date))) {
          runsByHour[hourKey] = {
            ...run,
            displayTime,
            hourKey
          };
          console.log(`Updated latest run for hour ${hourKey}:`, run.run_id, run.state);
        }
      });
      
      // Now add the latest run for each hour to our result
      Object.values(runsByHour).forEach(latestRun => {
        // Map Airflow states to our display format
        let status = latestRun.state || 'N/A';
        if (status === 'success') status = 'Completed';
        else if (status === 'failed') status = 'Failed';
        else if (status === 'running') status = 'Running';
        
        let runtime = latestRun.duration || '-';

        console.log(`Adding run to ${colKey} at ${latestRun.hourKey}: ${status} (${latestRun.displayTime})`);
        result[colKey][latestRun.hourKey] = { 
          status, 
          runtime, 
          displayTime: latestRun.displayTime,
          runId: latestRun.run_id
        };
      });
      
    } else {
      console.log(`No runs array found for DAG ${dag.dag_id}. Runs:`, dag.runs);
    }
  });
  
  console.log("%cAirflow transformation complete for Cluster Pool 1!", "color: green; font-weight: bold;");
  console.log("Final result:", result);
  return result;
}

const AirflowClusterPoolOne = ({ dynamicData }) => {
  const data = Array.isArray(dynamicData)
    ? transformAirflowData(dynamicData)
    : { FA: {}, Construction: {}, Common: {} };

  const nowEST = moment.tz("America/New_York");
  const currentHour = nowEST.clone().startOf("hour").format("h:mm A");
  const currentRowRef = useRef(null);

  useEffect(() => {
    if (currentRowRef.current) {
      currentRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, []);

  // Build fixed hourly slots from 12:00 AM to 11:00 PM
  const timeSlots = useMemo(() => {
    const start = moment.tz("America/New_York").startOf("day");
    return Array.from({ length: 24 }, (_, i) =>
      start.clone().add(i, "hours").format("h:mm A")
    );
  }, []);

  const columns = [
    { title: "FA", dataKey: "FA" },
    { title: "Common", dataKey: "Common" },
    { title: "Construction", dataKey: "Construction" },
  ];

  const getStatusStyle = (status) => {
    switch ((status || "").toLowerCase()) {
      case "completed":
        return { color: "green", fontWeight: "bold" };
      case "running":
      case "in progress":
        return { color: "blue", fontWeight: "bold" };
      case "failed":
      case "cancelled":
        return { color: "red", fontWeight: "bold" };
      case "scheduled":
        return { color: "#FFD700", fontWeight: "bold" };
      default:
        return {};
    }
  };

  function getDataForTime(colKey, slotTime) {
    // Check for exact match first
    const exact = data[colKey]?.[slotTime];
    if (exact) return exact;

    // Try to match by hour
    const slotHour = moment(slotTime, "h:mm A").hour();
    const matchKey = Object.keys(data[colKey] || {}).find(
      (k) => moment(k, "h:mm A").hour() === slotHour
    );
    if (matchKey) return data[colKey][matchKey];

    // Check if it's a future time slot
    const slotMoment = moment.tz(
      `${nowEST.format("M/D/YYYY")} ${slotTime}`,
      "M/D/YYYY h:mm A",
      "America/New_York"
    );
    if (slotMoment.isAfter(nowEST)) {
      return { status: "Scheduled", runtime: "", displayTime: slotTime };
    }

    return { status: "-", runtime: "-", displayTime: slotTime };
  }

  return (
    <Fragment>
      <Row className="mt-5">
        <Col lg={12}>
          <Card style={{ borderRadius: 8, overflow: "hidden" }}>
            <Card.Header>
              <Card.Title className="text-center" style={{ color: "#3D4465" }}>
                Cluster Pool 1
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <div
                style={{
                  maxHeight: 400,
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                <Table bordered style={{ borderRadius: 8, marginBottom: 0 }}>
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.title}
                          colSpan={3}
                          style={{
                            textAlign: "center",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#3D4465",
                            color: "white",
                            padding: 8,
                            zIndex: 4,
                          }}
                        >
                          <strong>{col.title}</strong>
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {columns.map((col) => (
                        <React.Fragment key={col.title}>
                          <th
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              top: 40,
                              backgroundColor: "#3D4465",
                              color: "white",
                              padding: 8,
                              zIndex: 3,
                            }}
                          >
                            Run Time
                          </th>
                          <th
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              top: 40,
                              backgroundColor: "#3D4465",
                              color: "white",
                              padding: 8,
                              zIndex: 3,
                            }}
                          >
                            Status
                          </th>
                          <th
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              top: 40,
                              backgroundColor: "#3D4465",
                              color: "white",
                              padding: 8,
                              zIndex: 3,
                            }}
                          >
                            Runtime
                          </th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((time) => {
                      const isCurrent = time === currentHour;
                      return (
                        <tr
                          key={time}
                          ref={isCurrent ? currentRowRef : null}
                          style={
                            isCurrent
                              ? {
                                  position: "sticky",
                                  top: 80,
                                  backgroundColor: "#d1e7dd",
                                  zIndex: 2,
                                }
                              : {}
                          }
                        >
                          {columns.map((col) => {
                            const { status, runtime, displayTime } =
                              getDataForTime(col.dataKey, time);
                            return (
                              <React.Fragment key={`${col.dataKey}-${time}`}>
                                <td style={{ textAlign: "center" }}>
                                  {displayTime}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <span style={getStatusStyle(status)}>
                                    {status === "Completed"
                                      ? "Succeeded"
                                      : status}
                                  </span>
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {runtime}
                                </td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default AirflowClusterPoolOne;
